import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Trash2 } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

import { Spinner } from '../Spinner';
import { Description } from './ModalConfirmation.styled';

interface ModalConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id: string | string[]) => void;
  itemId?: string | string[] | null;
  isLoading?: boolean;
  title?: string;
  description?: string;
  buttonConfirm?: string;
  needConfirm: boolean;
  confirmValue: string;
}

export const ModalConfirmation: FC<ModalConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemId,
  isLoading,
  title,
  buttonConfirm,
  description,
  needConfirm,
  confirmValue,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(needConfirm);

  useEffect(() => {
    if (needConfirm) {
      setIsConfirmDisabled(inputValue !== confirmValue);
    }
  }, [inputValue, confirmValue, needConfirm]);

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const handleConfirm = (): void => {
    if (!itemId) {
      onConfirm('');
    } else {
      onConfirm(itemId);
    }
  };

  const handleClose = (): void => {
    setInputValue('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose} backdrop="blur" size="2xl">
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1 text-lg pb-2">
              {title || 'Czy chcesz usunąć element?'}
            </ModalHeader>
            <ModalBody>
              <Description>
                {description
                        || `Ta operacja jest nieodwracalna. Usunięcie elementu spowoduje
                 trwałe usunięcie wszystkich powiązanych danych. Upewnij się, że chcesz kontynuować.`}
              </Description>
              {needConfirm && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">
                  To confirm enter:
                  <strong color="text-red">
                    {' '}
                    {confirmValue}
                  </strong>
                </p>
                <Input
                  placeholder="Enter value"
                  value={inputValue}
                  onValueChange={setInputValue}
                  variant="bordered"
                  size="lg"
                  radius="sm"
                />
              </div>
              )}
            </ModalBody>
            <ModalFooter className="pt-2">
              <Button
                color="default"
                variant="light"
                onPress={handleClose}
                radius="sm"
                size="sm"
              >
                Anuluj
              </Button>
              <Button
                radius="sm"
                size="sm"
                startContent={!buttonConfirm && (!isLoading ? <Trash2 size={14} color="#fff" /> : <Spinner />)}
                color={buttonConfirm ? 'primary' : 'danger'}
                onPress={handleConfirm}
                isDisabled={isConfirmDisabled || isLoading}
              >
                {buttonConfirm || 'Usuń'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
